import { Overlay, OverlayPositionBuilder, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { ComponentRef, Directive, ElementRef, HostListener, TemplateRef, DestroyRef, inject, input, computed } from "@angular/core";
import { ImageHintComponent } from "./image-hint.component";

@Directive({
    selector: '[imageHint]',
    standalone: false
})
export class ImageHintDirective {

    /**
    * This will be used to show tooltip or not
    * This can be used to show the tooltip conditionally
    */
    showHint = input<boolean>(true);

    //If this is specified then the specified text will be shown in the tooltip
    readonly image = input<{ url?: string; valid: boolean } | undefined>(
        undefined,
        {
            alias: 'imageHint'
        }
    );
    //If this is specified then specified template will be rendered in the tooltip
    contentTemplate = input<TemplateRef<any>>();

    private readonly destroyRef = inject(DestroyRef);

    private overlayRef?: OverlayRef;

    url = computed(() => {
        const image = this.image();

        return image && image.valid && image.url
            ? image.url
            : undefined;
    });

    constructor(
        private readonly overlay: Overlay,
        private readonly positionBuilder: OverlayPositionBuilder,
        private readonly elementRef: ElementRef
    ) {

        if (!this.showHint) {
            return;
        }

        //you can take the position as an input to adjust the position
        //, for now, it will show at the bottom always; but you can adjust your code
        //  as per your need
        const positionStrategy = this.positionBuilder
            .flexibleConnectedTo(this.elementRef)
            .withPositions([{
                originX: 'end',
                originY: 'center',
                overlayX: 'start',
                overlayY: 'center',
                offsetY: 5,
            }]);

        this.overlayRef = this.overlay.create({ positionStrategy });

        this.destroyRef.onDestroy(() => {
            this.closeHint();
        });
    }

    /**
     * This method will be called whenever the mouse enters in the Host element
     * i.e. where this directive is applied
     * This method will show the tooltip by instantiating the CustomToolTipComponent and attaching to the overlay
     */
    @HostListener('mouseenter')
    show() {
        const url = this.url();

        //attach the component if it has not already attached to the overlay
        if (url && this.overlayRef && !this.overlayRef.hasAttached()) {
            const hintRef: ComponentRef<ImageHintComponent> = this.overlayRef.attach(new ComponentPortal(ImageHintComponent));
            hintRef.instance.url = url;
            // hintRef.instance.contentTemplate = this.contentTemplate;
        }
    }

    /**
     * This method will be called when the mouse goes out of the host element
     * i.e. where this directive is applied
     * This method will close the tooltip by detaching the overlay from the view
     */
    @HostListener('mouseleave')
    hide() {
        this.closeHint();
    }

    /**
     * This method will close the tooltip by detaching the component from the overlay
     */
    private closeHint() {
        if (this.overlayRef) {
            this.overlayRef.detach();
        }
    }
}
